import { handleImportC1cStudy } from '@/entities/study';
import { showMessageError, showMessageSuccess } from '@/shared/lib';
import { useEffect, useState } from 'react';
import type { ChangeEvent } from 'react';
import { t } from 'ttag';

interface UseImportStudyModalParams {
  isOpen: boolean;
  onClose: () => void;
  onImported: () => void;
}

/**
 * Holds the CardioOne study ID field, the busy flag and the not-found error of the Import Study dialog. Clears all three whenever the dialog opens, and clears the error when the field is edited. Sends the trimmed value with its case kept; a NOT_FOUND refusal shows the field error, while UPSTREAM_ERROR and any thrown Apollo error show the generic toast and keep the dialog open. Success toasts, closes the dialog and calls onImported.
 *
 * @param isOpen - Whether the dialog is currently open.
 * @param onClose - Closes the dialog.
 * @param onImported - Called after a study was imported.
 * @returns The field value and handlers, the busy and disabled flags, and the field error text.
 */
export const useImportStudyModal = ({ isOpen, onClose, onImported }: UseImportStudyModalParams) => {
  const [externalId, setExternalId] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [isNotFound, setIsNotFound] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setExternalId('');
      setIsNotFound(false);
    }
  }, [isOpen]);

  const trimmedExternalId = externalId.trim();

  /**
   * Tracks edits to the CardioOne study ID field of the Import Study dialog.
   * Stores the typed value untrimmed and clears any not-found error shown under the field.
   *
   * @param event - Change event from the study ID input.
   * @returns Nothing.
   */
  const onChangeExternalId = (event: ChangeEvent<HTMLInputElement>) => {
    setExternalId(event.target.value);
    setIsNotFound(false);
  };

  /**
   * Imports the external study whose CardioOne ID was entered in the dialog.
   * Raises the busy flag, sends the trimmed ID to handleImportC1cStudy, then on success toasts,
   * closes the dialog and calls onImported, on NOT_FOUND shows the field error, and otherwise or
   * on a thrown error logs and toasts a generic failure, always lowering the busy flag.
   *
   * @returns A promise resolving once the import has settled and the busy flag is cleared.
   */
  const onClickImport = async () => {
    setIsImporting(true);
    try {
      const result = await handleImportC1cStudy(trimmedExternalId);
      if (result.isSuccess) {
        showMessageSuccess({ content: t`Study imported successfully.` });
        onClose();
        onImported();
      } else if (result.errorCode === 'NOT_FOUND') {
        setIsNotFound(true);
      } else {
        showMessageError({ content: t`Something went wrong. Please try again.` });
      }
    } catch (error) {
      console.error('Failed to import study', error);
      showMessageError({ content: t`Something went wrong. Please try again.` });
    } finally {
      setIsImporting(false);
    }
  };

  return {
    externalId,
    isImporting,
    isImportDisabled: trimmedExternalId === '',
    errorMessage: isNotFound ? t`We couldn’t find a study with this ID.` : undefined,
    onChangeExternalId,
    onClickImport,
  };
};
